import { fmtNum } from '../ui/ListFoot';
import { ToTop } from '../ui/ToTop';
import { arriveAt } from '../ui/arrive';
import { useTuck } from '../ui/useTuck';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { usePerRow } from './usePerRow';
import { navigate, useRoute } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { useRemembered } from '../lib/remember';
import { BookCard, BookTile } from '../ui/books';
import { Pill } from '../ui/bits';
import { Shelf, ShelfFoldAll, ViewLinks, useShelfView } from '../ui/controls';
import { Segmented } from '../ui/Segmented';
import { TabRow } from '../ui/TabRow';
import { bookLabel, dayText, todayIn } from '../book/model';
import { ListFoot } from '../ui/ListFoot';
import { CardActions } from './CardActions';
import { loadDetails, loadFilterFacts, loadReference, loadShelves, type Details, type Shelves } from './load';
import { ReferenceTab, backToTop, rememberReturn, type RefOrder, type RefView } from './Reference';
import type { RefBook } from './reference';
import { buildGroups, filterCount, libraryAlert, libraryOrder, matches, pruneSubgenres, recentFrom, referenceGroups, type AlertKey, type FilterFacts, type FilterLists, type FilterState } from './filters';
import { LibraryAlert } from './LibraryAlert';
import { FilterBar, FilterPanel } from './Filters';
import { useFlag } from '../shell/hooks';
import { keep } from '../lib/remember';
import {
  SHELVES,
  byReadType,
  cardLibrary,
  loanPillOf,
  copyPill,
  countText,
  finishedGroup,
  finishedLine,
  genreLine,
  finishedOrder,
  markPills,
  howLong,
  readingLine,
  readingOrder,
  readingPills,
  pacificDay,
  tileLine,
  tilePill,
  upNextLine,
  type ShelfBook,
  type ShelfDef,
} from './model';
import { LIB_STATES, needsSentence } from '../lib/libraryStates';
import './shelves.css';

// Shelves (Phase 6.2 Part 1, agreed with Kristi Oct 28): the Reading tab. Currently Reading
// (Paused at its foot) · Up Next · Future reads · Finished · Not for me · No shelf yet, each folding,
// with Show covers / Show cards and By read type; covers start at two rows with Show N more and
// Show all. Cards have Move ▾ and Start reading (the book page's own actions). Reference is Part 4.

/** The tab you were on, for coming back from a book (a page load starts on Reading). */
let lastTab: 'reading' | 'reference' = 'reading';

export const READING_DESCRIPTION = 'The books you’re reading, picking up next, want to read someday, and have read.';
export const REFERENCE_DESCRIPTION = 'Books you read intermittently or consult for research, rather than reading from beginning to end.';

type Load = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; shelves: Shelves };

export function ShelvesPage({ wide }: { wide: boolean }) {
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  // The shelves stay hidden until the first details arrive, so cards don't show titles alone first (Kristi, Oct 29).
  const [shown, setShown] = useState(false);
  // Shelves opens on Reading (Kristi, Oct 21); back from a book, it returns to the tab you were on.
  const [tab, setTabState] = useState<'reading' | 'reference'>(lastTab);
  const setTab = (t: 'reading' | 'reference') => {
    lastTab = t;
    setTabState(t);
    setFiltersOpen(false);
  };
  const wanted = useRef(new Map<string, boolean>()); // book id → wants card details
  const asked = useRef(new Set<string>());
  const timer = useRef<number | undefined>(undefined);
  const today = todayIn();
  // Filters (Part 3): never remembered, so Shelves always opens with none on (a refresh too).
  // Filters stick on this device, so a look at a book and back keeps them (Kristi, Oct 28).
  const [filters, setFilters] = useRemembered<FilterState>('shelves:filters', {});
  const [filtersOpen, setFiltersOpen] = useState(false);
  // On a phone, the tabs and Filters slide away as you scroll down and come back as you scroll up,
  // leaving room for what's pinned below them (Kristi, Oct 29). Laptop and iPad keep them.
  const tucked = useTuck(wide);
  // The pinned bar's height, so A–Z's controls can pin just under it (Kristi, Oct 29).
  const topEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topEl.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--shelves-top-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const [facts, setFacts] = useState<{ facts: FilterFacts[]; lists: FilterLists } | null>(null);
  // Reference (Part 4): its books, its own filters (remembered apart from Reading's), A–Z or By genre, covers or list.
  const [ref, setRef] = useState<{ heads: ShelfBook[]; books: RefBook[] } | null>(null);
  const [refFiltersKept, setRefFilters] = useRemembered<FilterState>('shelves:ref-filters', {});
  // Genres and subgenres are picked in the views on Reference (Kristi, Oct 29), so older kept ones are ignored.
  const refFilters = useMemo(() => {
    const { subgenre: _sub, genre: _genre, ...rest } = refFiltersKept;
    return rest as FilterState;
  }, [refFiltersKept]);
  const [refOrder, setRefOrder] = useRemembered<RefOrder>('ref:order', 'genre');
  const [refView, setRefView] = useRemembered<RefView>('ref:view', 'covers');

  const load = () =>
    loadShelves()
      .then((shelves) => {
        // Keep what's showing while each shelf's details are read again (no flicker after a change).
        asked.current = new Set();
        setState({ kind: 'ready', shelves });
        // Nothing showing needs details (empty shelves), or they're slow: show the shelves anyway.
        window.setTimeout(() => setShown(true), 2500);
        // Then the Reference books, and what every book offers the filters, after the shelves show.
        void loadReference()
          .catch(() => null)
          .then((r) => {
            if (r) setRef(r);
            return loadFilterFacts(shelves, todayIn(), r?.heads ?? []);
          })
          .then(setFacts)
          .catch(() => {
            /* the filters say they're loading; the shelves still work */
          });
      })
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }));

  useEffect(() => {
    void load();
    // No count on the Reference tab (Kristi, Oct 29: Reading has none, and the labels stay short).
    // A change anywhere (a card, the side panel, Add a book): read the shelves again.
    const again = () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), 250);
    };
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, []);

  // Shelves say which books are showing; their details are read in one batch just after.
  const ready = useRef<Shelves | null>(null);
  ready.current = state.kind === 'ready' ? state.shelves : null;
  const flushing = useRef(false);
  const need = (books: ShelfBook[], cards: boolean) => {
    for (const b of books) {
      if (asked.current.has(b.id + ':c') || (!cards && asked.current.has(b.id))) continue;
      wanted.current.set(b.id, cards || wanted.current.get(b.id) === true);
    }
    if (wanted.current.size && !flushing.current) {
      flushing.current = true;
      window.setTimeout(flush, 0);
    }
  };
  const flush = () => {
    flushing.current = false;
    const shelves = ready.current;
    if (!shelves || !wanted.current.size) return;
    const all = Object.values(shelves).flat();
    const byId = new Map(all.map((b) => [b.id, b]));
    const list = [...wanted.current.keys()].map((id) => byId.get(id)).filter((b): b is ShelfBook => Boolean(b));
    const cards = new Set([...wanted.current].filter(([, c]) => c).map(([id]) => id));
    for (const b of list) asked.current.add(b.id + (cards.has(b.id) ? ':c' : ''));
    for (const b of list) asked.current.add(b.id);
    wanted.current = new Map();
    loadDetails(list, cards)
      .then((m) => setDetails((old) => new Map([...old, ...m])))
      .catch(() => {
        /* the shelf still shows titles; details come on the next load */
      })
      .finally(() => setShown(true));
  };

  const { path } = useRoute();
  const open = (b: ShelfBook) => () => navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });

  const from = facts ? recentFrom(today, facts.lists.recentDays) : today;
  // The Reading tab's facts leave out Reference-only books; Reference has its own.
  const readingFacts = useMemo(() => facts?.facts.filter((f) => f.shelf !== 'reference') ?? [], [facts]);
  const refIds = useMemo(() => new Set(ref?.books.map((b) => b.id) ?? []), [ref]);
  const refFacts = useMemo(() => facts?.facts.filter((f) => refIds.has(f.id)) ?? [], [facts, refIds]);
  const groups = useMemo(() => (facts ? buildGroups(readingFacts, facts.lists, filters, from) : []), [facts, readingFacts, filters, from]);
  const on = filterCount(filters) > 0;
  const matching = useMemo(() => (on && facts ? new Set(readingFacts.filter((f) => matches(f, filters, from)).map((f) => f.id)) : null), [on, facts, readingFacts, filters, from]);
  // The library alert line (Part 5): a tapped part shows its books through one Library chip; the
  // filters you had are kept, and come back with Back to your filters, ✕ on that chip, or Clear.
  const [fromAlert, setFromAlert] = useRemembered<{ kept: FilterState; chip: AlertKey } | null>('shelves:from-alert', null);
  const alertParts = useMemo(() => libraryAlert(readingFacts), [readingFacts]);
  const readingNeeds = needsSentence(alertParts);
  // Library chips remembered from before the one list of states (R2): "borrowed" is now "on_loan"
  // or "not_started", and so on; ids that no longer exist are dropped so they can't hide every book.
  useEffect(() => {
    const on = filters.library?.on;
    if (!on?.length) return;
    const kept = on.filter((id) => (LIB_STATES as readonly string[]).includes(id));
    if (kept.length !== on.length) setFilters({ ...filters, library: { ...filters.library!, on: kept } });
  }, []);
  const backFromAlert = () => {
    if (!fromAlert) return;
    setFilters(fromAlert.kept);
    setFromAlert(null);
  };
  const pickAlert = (k: AlertKey) => {
    setFromAlert({ kept: fromAlert?.kept ?? filters, chip: k });
    const next: FilterState = { library: { mode: 'show', on: [k] } };
    setFilters(next);
    setFiltersOpen(false);
    // Arriving from the line (filters review F11, Kristi Nov 2): open each folded shelf holding its
    // books, then scroll to the first, so the result is in view. Other shelves stay as they were.
    if (state.kind !== 'ready') return;
    const hit = new Set(readingFacts.filter((f) => matches(f, next, from)).map((f) => f.id));
    const keys = SHELVES.filter((d) => state.shelves[d.key].some((b) => hit.has(b.id))).map((d) => `reading-${d.key}`);
    if (keys.length) arriveAt(`[data-shelf="${keys[0]}"]`, keys);
  };
  const change = (next: FilterState) => {
    const pruned = facts ? pruneSubgenres(next, facts.lists) : next;
    // Clear all always clears (F11, Kristi Nov 2); ✕ on the line's chip goes back to your filters.
    if (fromAlert && !Object.keys(pruned).length) {
      setFromAlert(null);
      return setFilters({});
    }
    if (fromAlert && !pruned.library?.on.includes(fromAlert.chip)) return backFromAlert();
    setFilters(pruned);
  };
  const refGroups = useMemo(() => (facts ? referenceGroups(buildGroups(refFacts, facts.lists, refFilters, from)) : []), [facts, refFacts, refFilters, from]);
  const refOn = filterCount(refFilters) > 0;
  const refBooks = useMemo(() => {
    if (!ref || !facts) return [];
    const byId = new Map(refFacts.map((f) => [f.id, f]));
    return ref.books
      .map((b) => ({ ...b, genres: byId.get(b.id)?.genres ?? [], subgenres: byId.get(b.id)?.subgenres ?? [] }))
      .filter((b) => !refOn || (byId.get(b.id) && matches(byId.get(b.id)!, refFilters, from)));
  }, [ref, facts, refFacts, refOn, refFilters, from]);
  const refAll = useMemo(() => {
    if (!ref || !facts) return [];
    const byId = new Map(refFacts.map((f) => [f.id, f]));
    return ref.books.map((b) => ({ ...b, genres: byId.get(b.id)?.genres ?? [], subgenres: byId.get(b.id)?.subgenres ?? [] }));
  }, [ref, facts, refFacts]);
  const changeRef = (next: FilterState) => setRefFilters(facts ? pruneSubgenres(next, facts.lists) : next);
  const openRef = (b: RefBook) => () => (wide || rememberReturn(b.id), navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide }));
  const libraryOn = Boolean(filters.library?.on.length);
  const shelfBooks = (key: keyof Shelves) => {
    if (state.kind !== 'ready') return [];
    const list = state.shelves[key];
    if (!matching) return list;
    const kept = list.filter((b) => matching.has(b.id));
    return libraryOn && key === 'up_next' ? libraryOrder(kept.map((b) => ({ ...b, ...details.get(b.id) }))) : kept;
  };

  return (
    <div class={`page shelves${tucked && !filtersOpen ? ' tucked' : ''}`}>
      {wide ? <h1 class="visually-hidden">Shelves</h1> : null}
      <div class="shelves-top" ref={topEl}>
        {/* The one tab row (R9): the teal dot on Reading while the library line shows (R7 N1, Kristi Nov 2: answer 3a), so it shows from Reference too. */}
        <TabRow
          label="Shelves"
          value={tab}
          onPick={setTab}
          tabs={[
            { key: 'reading', name: 'Reading', need: readingNeeds || undefined },
            { key: 'reference', name: 'Reference' },
          ]}
        />
        {tab === 'reading' && state.kind === 'ready' ? (
          <>
            <span class="shelves-divider" aria-hidden="true" />
            <FilterBar groups={groups} state={filters} open={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} onChange={change} showing={matching ? matching.size : null} />
            <span class="shelves-top-end">
              <ShelfFoldAll />
              {/* ↑ Top on long pages (check-up, Kristi Nov 2): at the right of the pinned bar. */}
              <ToTop wide={wide} />
            </span>
          </>
        ) : null}
        {tab === 'reference' && ref ? (
          <>
            <span class="shelves-divider" aria-hidden="true" />
            <FilterBar groups={refGroups} state={refFilters} open={filtersOpen} onToggle={() => setFiltersOpen(!filtersOpen)} onChange={changeRef} showing={refOn && facts ? refBooks.length : null} />
            <span class="view-links ref-links">
              <Segmented label="Group" options={[['genre', 'By genre'], ['az', 'A–Z']]} value={refOrder} onChange={setRefOrder} />
              <Segmented label="Show as" options={[['covers', 'Covers'], ['list', 'List']]} value={refView} onChange={setRefView} />
            </span>
            <span class="shelves-top-end">
              <ShelfFoldAll />
              {/* ↑ Top on long pages (check-up, Kristi Nov 2): at the right of the pinned bar. */}
              <ToTop wide={wide} />
            </span>
          </>
        ) : null}
        {/* The panel drops down from the pinned bar, so it opens where you are (Kristi, Oct 28). */}
        {tab === 'reading' && filtersOpen && state.kind === 'ready' ? (
          facts ? (
            <FilterPanel groups={groups} state={filters} onChange={change} onClose={() => setFiltersOpen(false)} showing={matching ? matching.size : readingFacts.length} />
          ) : (
            <p class="shelves-quiet">Loading the filters…</p>
          )
        ) : null}
        {tab === 'reference' && filtersOpen && ref ? (
          facts ? (
            <FilterPanel groups={refGroups} state={refFilters} onChange={changeRef} onClose={() => setFiltersOpen(false)} showing={refBooks.length} />
          ) : (
            <p class="shelves-quiet">Loading the filters…</p>
          )
        ) : null}
      </div>
      {/* Each tab says what it's for, under the tabs (Kristi, Oct 31), as on the Queue and My books. */}
      <p class="queue-description">{tab === 'reading' ? READING_DESCRIPTION : REFERENCE_DESCRIPTION}</p>
      {tab === 'reading' && state.kind === 'ready' && facts ? (
        <LibraryAlert parts={alertParts} showing={fromAlert?.chip ?? null} left={matching ? matching.size : null} onPick={pickAlert} onBack={backFromAlert} />
      ) : null}
      {tab === 'reading' && on && matching && !matching.size && !fromAlert ? <div class="heads-up">No books match these filters. Clear one, or Clear them all.</div> : null}
      {tab === 'reference' ? (
        ref && facts ? (
          <ReferenceTab books={refBooks} all={refAll} lists={facts.lists} order={refOrder} view={refView} wide={wide} open={openRef} />
        ) : state.kind === 'error' ? (
          <div class="heads-up">Couldn’t load your shelves: {state.message}</div>
        ) : (
          <p class="shelves-quiet">Loading your Reference books…</p>
        )
      ) : state.kind === 'loading' ? (
        <p class="shelves-quiet">Loading your shelves…</p>
      ) : state.kind === 'error' ? (
        <div class="heads-up">Couldn’t load your shelves: {state.message}</div>
      ) : on && !facts ? (
        // Remembered filters: wait for them rather than flash every book first.
        <p class="shelves-quiet">Loading your filters…</p>
      ) : (
        <>
        {!shown ? <p class="shelves-quiet">Loading your shelves…</p> : null}
        <div class={`shelves-list${shown ? '' : ' waiting'}`} aria-busy={!shown}>
          {SHELVES.map((def) => (
            <ShelfBlock key={def.key} def={def} books={shelfBooks(def.key)} details={details} need={need} open={open} wide={wide} today={today} />
          ))}
        </div>
        </>
      )}
      <ToTop wide={wide} float />
    </div>
  );
}

function ShelfBlock({
  def,
  books: raw,
  details,
  need,
  open,
  wide,
  today,
}: {
  def: ShelfDef;
  books: ShelfBook[];
  details: Map<string, Details>;
  need: (books: ShelfBook[], cards: boolean) => void;
  open: (b: ShelfBook) => () => void;
  wide: boolean;
  today: string;
}) {
  const view = useShelfView(def.key, { show: def.show, arrange: 'one' });
  // Reading's shelves link to the Queue's list (Kristi, Oct 29), once the Queue has that tab.
  const queueOn = useFlag('queue');
  const inQueue = queueOn && (def.key === 'up_next' || def.key === 'future_reads');
  const myBooksOn = useFlag('my_books') === true;
  const { ref, perRow, cardRow } = usePerRow(!wide);
  const [pausedOpen, setPausedOpen] = useRemembered('shelves:paused-open', false);
  // How much each group shows, in steps of two rows (or the shelf's cards); 'all' = Show all.
  const [pagesBy, setPagesBy] = useState<Record<string, number | 'all'>>({});
  // By read type, each group starts at one row (Kristi, Oct 28); one order starts at two.
  const pagesOf = (id: string) => pagesBy[id] ?? (view.arrange === 'readtype' ? 0 : 1);
  const setPagesOf = (id: string) => (p: number | 'all') => setPagesBy((old) => ({ ...old, [id]: p }));

  // Merge what step 2 found into each book.
  const books = useMemo(() => {
    const merged = raw.map((b) => {
      const d = details.get(b.id);
      return d ? { ...b, ...d, reading: d.reading ?? b.reading } : b;
    });
    return def.key === 'finished' ? finishedOrder(merged) : merged;
  }, [raw, details, def.key]);

  const cards = view.show === 'cards';
  // Two rows to start (Up Next: 12 cards, 6 on a phone); Currently Reading shows every card.
  const rowLen = cards ? cardRow : perRow;
  const step = def.key === 'currently_reading' && cards ? Infinity : cards && def.firstCards ? (wide ? def.firstCards.wide : def.firstCards.narrow) : rowLen * 2;
  // Kristi, Oct 28: with Show all on, or more than 4 rows showing, the header offers
  // "Show first rows only", which goes back to two rows in either view.
  const expanded = Object.values(pagesBy).some((p) => p === 'all' || (p > 0 && p * step > rowLen * 4));
  // Changing a view starts the shelf over at its first rows.
  const viewKey = `${view.show}:${view.arrange}`;
  const lastView = useRef(viewKey);
  if (lastView.current !== viewKey) {
    lastView.current = viewKey;
    if (Object.keys(pagesBy).length) setPagesBy({});
  }
  const total = books.length;
  const paused = def.key === 'currently_reading' ? readingOrder(books).paused : [];
  const main = def.key === 'currently_reading' ? readingOrder(books).reading : books;
  const groups = view.arrange === 'readtype' ? byReadType(main) : [{ key: 'all', label: '', books: main }];

  // Currently Reading always needs its reads (paused or not, %), whichever view.
  if (def.key === 'currently_reading') need(books, true);
  if (!total) return null; // empty shelves don't show (decisions doc › Phone Shelves)

  return (
    <Shelf
      id={`reading-${def.key}`}
      name={def.name}
      count={countText(def.key, total, paused.length)}
      description={def.description}
      startOpen={def.open}
      // The heading stays pinned while you scroll through the shelf; the description scrolls away (Kristi, Oct 29).
      pinHead
      seeAll={
        // Its to-do home (filters review, Kristi Nov 2): Update details' first list, on the heading.
      def.key === 'no_status' && myBooksOn ? (
          <a
            class="btn-link shelf-queue-link"
            href="/my-books/details"
            onClick={(e) => {
              e.preventDefault();
              keep('queue:review:chip', 'shelf');
              navigate('/my-books/details');
            }}
          >
            {total === 1 ? 'Give it a shelf in Update details ›' : 'Give them a shelf in Update details ›'}
          </a>
      ) : undefined
      }
      view={
        <>
          <ViewLinks view={view} oneOrderName={def.oneOrder} />
          {inQueue ? (
            <a
              class="btn-link shelf-queue-link"
              href="/queue"
              onClick={(e) => {
                e.preventDefault();
                keep('queue:tab', def.key);
                navigate('/queue');
              }}
            >
              Change your reading order in Queue ›
            </a>
          ) : null}
          {expanded ? (
            <button type="button" class="btn-link shelf-first-rows" onClick={() => {
              setPagesBy(Object.fromEntries(groups.map((g) => [`${def.key}:${g.key}`, 0])));
              backToTop(`[data-shelf="reading-${def.key}"]`);
            }}>
              Show first rows only
            </button>
          ) : null}
        </>
      }
    >
      <div ref={ref} class="shelf-groups">
        {groups.map((g) => (
          <Group
            key={g.key}
            id={`${def.key}:${g.key}`}
            label={g.label}
            def={def}
            books={g.books}
            cards={cards}
            step={step}
            firstRows={view.arrange === 'readtype' ? rowLen : rowLen * 2}
            pages={pagesOf(`${def.key}:${g.key}`)}
            setPages={setPagesOf(`${def.key}:${g.key}`)}
            need={need}
            open={open}
            today={today}
            phone={!wide}
          />
        ))}
        {paused.length ? (
          <div class="shelf-paused">
            <button type="button" class="shelf-paused-toggle" aria-expanded={pausedOpen} onClick={() => setPausedOpen(!pausedOpen)}>
              <span class={`fold${pausedOpen ? ' open' : ''}`} aria-hidden="true">
                ▸
              </span>
              {paused.length} paused
            </button>
            {pausedOpen ? <Group id="currently_reading:paused" label="" def={def} books={paused} cards={cards} step={Infinity} firstRows={Infinity} pages={1} setPages={() => {}} need={need} open={open} today={today} phone={!wide} faded /> : null}
          </div>
        ) : null}
      </div>
    </Shelf>
  );
}

function Group({
  id,
  label,
  def,
  books,
  cards,
  step,
  firstRows,
  pages,
  setPages,
  need,
  open,
  today,
  phone,
  faded,
}: {
  id: string;
  label: string;
  def: ShelfDef;
  books: ShelfBook[];
  cards: boolean;
  step: number;
  /** Two rows of what's showing (0 pages = "Show first rows only"). */
  firstRows: number;
  pages: number | 'all';
  setPages: (p: number | 'all') => void;
  need: (books: ShelfBook[], cards: boolean) => void;
  open: (b: ShelfBook) => () => void;
  today: string;
  phone: boolean;
  faded?: boolean;
}) {
  // How many are showing: the start (two rows, or the shelf's cards), then more. Show all
  // reveals the rest a few rows at a time as you scroll, so 2,000 covers stay quick.
  // Counted in steps (two rows each), so the number follows the window when it's resized.
  const shown = pages === 'all' ? 'all' : pages === 0 ? Math.min(firstRows, step) : pages * step;
  const [allUpTo, setAllUpTo] = useState(0);
  const count = shown === 'all' ? Math.min(books.length, Math.max(allUpTo, step * 4)) : Math.min(shown, books.length);
  const visible = books.slice(0, count);
  need(visible, cards || def.key === 'currently_reading');
  // From "first rows only", Show N more goes back up to the shelf's usual step.
  const nextStep = pages === 0 && firstRows < step ? step - count : step;
  // The foot's step: from first rows only, what Show N more adds (never so big the foot hides).
  const footPage = pages === 0 && count < books.length ? Math.min(nextStep, books.length - 1) : step;

  // Show all: more as the end of the shelf comes into view.
  const end = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (shown !== 'all' || count >= books.length || !end.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setAllUpTo((n) => Math.max(n, count) + step * 4), { rootMargin: '600px' });
    io.observe(end.current);
    return () => io.disconnect();
  }, [shown, count, books.length]);

  const rankOf = (b: ShelfBook) => books.indexOf(b) + 1;
  // Each read type folds, remembered on this device; its count shows beside the name (Kristi, Oct 28).
  const [groupOpen, setGroupOpen] = useRemembered(`fold:group:${id}`, true);
  let lastMonth = '';
  return (
    <div class="shelf-group" data-group={id}>
      {label ? (
        <button type="button" class="group-label group-toggle" aria-expanded={groupOpen} onClick={() => setGroupOpen(!groupOpen)}>
          <span class={`fold${groupOpen ? ' open' : ''}`} aria-hidden="true">
            ▸
          </span>
          {label} <span class="group-count">· {books.length === 1 ? '1 book' : `${fmtNum(books.length)} books`}</span>
        </button>
      ) : null}
      {groupOpen || !label ? (
        <>
      {cards ? (
        <div class="cards">
          {visible.map((b) => {
            // The library pill shows on every shelf's cards, with the card's Library copy bar (Kristi, Oct 28).
            const own = b.reading ? readingPills(b, today) : def.key === 'up_next' ? [copyPill(b, today)] : [loanPillOf(b, today)];
            const pills = [...own.filter((p): p is string => Boolean(p)), ...markPills(b)];
            // "Finished Mar 2024" on its own line, apart from ↻ Read again (Kristi, Oct 28).
            const finished = def.key === 'finished' ? null : finishedLine(b.lastFinished);
            const line =
              def.key === 'currently_reading'
                ? readingLine(b, today)
                : def.key === 'up_next' || def.key === 'future_reads'
                  ? upNextLine(b, rankOf(b), today, def.name)
                  : def.key === 'finished'
                    ? `Finished ${finishedGroup(b.finished)}`
                    : def.key === 'no_status'
                      ? `Added ${dayText(pacificDay(b.addedAt), today)} · ${howLong(pacificDay(b.addedAt), today)} ago`
                      : b.notForMe?.kind
                        ? `${b.notForMe.kind === 'abandoned' ? 'Abandoned' : 'Passed on'}${b.notForMe.on ? ` ${dayText(b.notForMe.on, today)}` : ''}`
                        : '';
            return (
              <BookCard
                key={b.id}
                book={{
                  id: b.id,
                  title: b.title,
                  author: b.author,
                  series: b.series ?? undefined,
                  coverUrl: b.cover,
                  readType: b.readType,
                  genre: genreLine(b) ?? undefined,
                  library: cardLibrary(b, today),
                }}
                pills={pills.length ? pills.map((p) => <Pill key={p}>{p}</Pill>) : undefined}
                notOwned={b.owned === false}
                line={[line, finished ?? '']}
                progress={b.reading && !b.reading.pausedOn && b.reading.percent !== null ? b.reading.percent : undefined}
                note={b.note ?? undefined}
                faded={faded || def.key === 'not_for_me'}
                onOpen={open(b)}
                actions={<CardActions b={b} today={today} phone={phone} />}
              />
            );
          })}
        </div>
      ) : (
        <div class={`tiles${def.key === 'not_for_me' ? ' greyed' : ''}`}>
          {visible.map((b) => {
            const month = def.key === 'finished' ? finishedGroup(b.finished) : '';
            const caption = month && month !== lastMonth ? month : '';
            lastMonth = month || lastMonth;
            return (
              <div key={b.id} class="tile-wrap">
                {def.key === 'finished' ? <span class={`tile-month${caption ? '' : ' blank'}`}>{caption || ' '}</span> : null}
                <BookTile
                  book={{ id: b.id, title: b.title, author: b.author, coverUrl: b.cover, readType: b.readType, subgenre: b.subgenre ?? undefined }}
                  line={tileLine(def.key, b, today)}
                  libraryPill={tilePill(b, today)}
                  notOwned={b.owned === false}
                  onOpen={open(b)}
                />
              </div>
            );
          })}
          <span ref={end} class="tiles-end" aria-hidden="true" />
        </div>
      )}
      {/* The one list foot (R5 B6, R7 N7): Showing 12 of 60 · Show 12 more · Show all 60 · Show fewer. */}
      <ListFoot
        shown={shown === 'all' ? books.length : count}
        total={books.length}
        page={footPage}
        onMore={() => setPages(pages === 'all' ? 'all' : pages === 0 ? (firstRows >= step ? 2 : 1) : pages + 1)}
        onAll={() => setPages('all')}
        onFewer={() => {
          setPages(1);
          backToTop(`[data-group="${id}"]`);
        }}
      />
        </>
      ) : null}
    </div>
  );
}
